import { useEffect, useRef, useCallback } from 'react';

class Ring {
  constructor(x, y, delay, color, maxR, speed, lineWidth) {
    this.x = x; this.y = y;
    this.r = 0; this.maxR = maxR;
    this.delay = delay; this.elapsed = 0;
    this.color = color; this.alpha = 1;
    this.speed = speed; this.lineWidth = lineWidth;
    this.done = false;
  }
  update(dt) {
    this.elapsed += dt;
    if (this.elapsed < this.delay) return;
    this.r += this.speed;
    this.alpha = Math.max(0, 1 - this.r / this.maxR);
    if (this.r >= this.maxR) this.done = true;
  }
  draw(ctx) {
    if (this.elapsed < this.delay || this.alpha <= 0 || this.r <= 0) return;
    ctx.save();
    ctx.beginPath();
    ctx.arc(this.x, this.y, Math.max(0.1, this.r), 0, Math.PI * 2);
    ctx.strokeStyle = this.color.replace(')', `,${Math.max(0, this.alpha)})`).replace('rgb', 'rgba');
    ctx.lineWidth = Math.max(0.1, this.lineWidth * this.alpha);
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 20 * this.alpha;
    ctx.stroke();
    ctx.restore();
  }
}

class Particle {
  constructor(x, y, angle, speed, color, size, life) {
    this.x = x; this.y = y;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed;
    this.color = color; this.size = size;
    this.life = life; this.maxLife = life;
    this.done = false;
    this.trail = [];
  }
  update() {
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > 6) this.trail.shift();
    this.x += this.vx;
    this.y += this.vy;
    this.vx *= 0.96;
    this.vy *= 0.96;
    this.life--;
    if (this.life <= 0) this.done = true;
  }
  draw(ctx) {
    const alpha = Math.max(0, this.life / this.maxLife);
    if (alpha <= 0) return;
    ctx.save();
    // Trail
    for (let i = 0; i < this.trail.length - 1; i++) {
      const ta = (i / this.trail.length) * alpha * 0.5;
      if (ta <= 0) continue;
      ctx.beginPath();
      ctx.moveTo(this.trail[i].x, this.trail[i].y);
      ctx.lineTo(this.trail[i + 1].x, this.trail[i + 1].y);
      ctx.strokeStyle = this.color.replace(')', `,${ta})`).replace('rgb', 'rgba');
      ctx.lineWidth = Math.max(0.1, this.size * (i / this.trail.length));
      ctx.stroke();
    }
    // Main dot
    const r = Math.max(0.1, this.size * alpha);
    ctx.beginPath();
    ctx.arc(this.x, this.y, r, 0, Math.PI * 2);
    ctx.fillStyle = this.color.replace(')', `,${alpha})`).replace('rgb', 'rgba');
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8 * alpha;
    ctx.fill();
    ctx.restore();
  }
}

class Flash {
  constructor(x, y) {
    this.x = x; this.y = y;
    this.alpha = 1; this.r = 0; this.done = false;
  }
  update() {
    this.r += 8;
    this.alpha -= 0.06;
    if (this.alpha <= 0) this.done = true;
  }
  draw(ctx) {
    if (this.alpha <= 0) return;
    ctx.save();
    const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.r);
    grad.addColorStop(0, `rgba(255, 255, 255, ${this.alpha})`);
    grad.addColorStop(0.3, `rgba(0, 240, 255, ${this.alpha * 0.8})`);
    grad.addColorStop(1, `rgba(0, 240, 255, 0)`);
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.restore();
  }
}

export default function ClickWowEffect({ onScrollToProtocol }) {
  const canvasRef = useRef(null);
  const ringsRef = useRef([]);
  const particlesRef = useRef([]);
  const flashesRef = useRef([]);
  const animRef = useRef(null);
  const lastTimeRef = useRef(null);

  const triggerEffect = useCallback((x, y) => {
    // Flash at center
    flashesRef.current.push(new Flash(x, y));

    // Concentric rings with staggered delays
    const ringConfigs = [
      { delay: 0, maxR: 120, speed: 6, color: 'rgb(0, 240, 255)', lw: 3 },
      { delay: 40, maxR: 200, speed: 7, color: 'rgb(74, 144, 217)', lw: 2 },
      { delay: 80, maxR: 320, speed: 8, color: 'rgb(0, 240, 255)', lw: 1.5 },
      { delay: 120, maxR: 480, speed: 9, color: 'rgb(255, 255, 255)', lw: 1 },
      { delay: 200, maxR: 650, speed: 10, color: 'rgb(0, 240, 255)', lw: 0.8 },
    ];
    ringConfigs.forEach(({ delay, maxR, speed, color, lw }) => {
      ringsRef.current.push(new Ring(x, y, delay, color, maxR, speed, lw));
    });

    // Dense particle burst
    const colors = ['rgb(0, 240, 255)', 'rgb(74, 144, 217)', 'rgb(255, 255, 255)', 'rgb(0, 255, 136)', 'rgb(100, 200, 255)'];
    for (let i = 0; i < 70; i++) {
      const angle = (i / 70) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      const speed = 4 + Math.random() * 8;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = 1.5 + Math.random() * 3;
      const life = 30 + Math.floor(Math.random() * 30);
      particlesRef.current.push(new Particle(x, y, angle, speed, color, size, life));
    }
    // Extra close burst of brighter particles
    for (let i = 0; i < 20; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      particlesRef.current.push(new Particle(x, y, angle, speed, 'rgb(255, 255, 255)', 2 + Math.random() * 2, 25 + Math.random() * 15));
    }

    // Scroll to protocol after delay
    setTimeout(() => {
      if (onScrollToProtocol) onScrollToProtocol();
    }, 700);
  }, [onScrollToProtocol]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const animate = (time) => {
      if (!lastTimeRef.current) lastTimeRef.current = time;
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      flashesRef.current.forEach(f => { f.update(); f.draw(ctx); });
      flashesRef.current = flashesRef.current.filter(f => !f.done);

      ringsRef.current.forEach(r => { r.update(dt); r.draw(ctx); });
      ringsRef.current = ringsRef.current.filter(r => !r.done);

      particlesRef.current.forEach(p => { p.update(); p.draw(ctx); });
      particlesRef.current = particlesRef.current.filter(p => !p.done);

      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const handleClick = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    triggerEffect(e.clientX - rect.left, e.clientY - rect.top);
  }, [triggerEffect]);

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 10, cursor: 'crosshair' }}
      data-testid="click-effect-canvas"
    />
  );
}
